import { computed, onUnmounted, readonly, ref, watch, type Ref } from 'vue';
import { isAppVisible } from '@/libs/app-visibility';
import { useIsActive } from './active';

/**
 * The current time in seconds, refreshed every `intervalMs` while the calling
 * component is active and the app visible: everything computed from it then
 * stops with it, in a page kept alive or an app in the background.
 */
export function useClock(intervalMs = 5000): Readonly<Ref<number>> {
  const now = ref(Date.now() / 1000);
  const isActive = useIsActive();
  const running = computed(() => isActive.value && isAppVisible.value);
  let timer: ReturnType<typeof setInterval> | undefined;

  function stop() {
    clearInterval(timer);
    timer = undefined;
  }

  watch(
    running,
    isRunning => {
      stop();
      if (!isRunning) return;
      // Back on screen, the time is as stale as the time spent away.
      now.value = Date.now() / 1000;
      timer = setInterval(() => (now.value = Date.now() / 1000), intervalMs);
    },
    { immediate: true },
  );
  onUnmounted(stop);

  return readonly(now);
}
